import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faCircleCheck,
  faCircleExclamation,
  faHeartPulse,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { SkeletonTable } from '@/components/skeletons';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  fetchPlatformHealth,
  type HealthStatus,
  type PlatformHealth,
  type PlatformHealthProbe,
} from './api/platform-operations.api';

/**
 * Phase 12.7b Wave 1 — Platform Console → Operations → Monitoring.
 *
 * Governance: **W1-Q1 = A — LIVE-ONLY.**
 *
 * ── WHAT THIS PAGE IS NOT ──────────────────────────────────────────────────
 * No history. No uptime. No trend. No alerting, alert rules or acknowledgement.
 * No scheduled collection and no persistence of any kind — nothing here survives
 * a refresh, because nothing is stored. Those were explicitly excluded, and the
 * page must not imply them by drawing a chart over a single sample.
 *
 * ── THE LIMIT THIS PAGE STATES OUT LOUD ────────────────────────────────────
 * Queue processors run **inside the API container** (`entrypoint.sh` starts only
 * `node dist/main`; no worker service exists in either compose file). So this
 * endpoint is served by the very process that also runs the workers — it
 * **cannot independently prove worker-process liveness**: if that process were
 * down, the page would not load at all.
 *
 * The probes prove *reachability of a dependency from the API*, which is a real
 * and useful thing. They do not prove *a consumer is draining a queue*. The page
 * says so in as many words rather than letting a green tick imply it.
 */

const STATUS_META: Record<
  HealthStatus,
  { label: string; icon: typeof faCircleCheck; tone: string }
> = {
  healthy: {
    label: 'Healthy',
    icon: faCircleCheck,
    tone: 'text-emerald-600 dark:text-emerald-400',
  },
  degraded: {
    label: 'Degraded',
    icon: faTriangleExclamation,
    tone: 'text-amber-600 dark:text-amber-400',
  },
  down: { label: 'Down', icon: faCircleExclamation, tone: 'text-red-600 dark:text-red-400' },
};

/** Human labels for the probes the deployed service actually returns. */
const PROBE_LABELS: Record<string, string> = {
  database: 'Database',
  redis: 'Redis',
  emailGateway: 'Email Gateway',
  queueSystem: 'Queue Transport',
  storage: 'Storage',
};

/** What each probe genuinely establishes — no invented signal. */
const PROBE_MEANING: Record<string, string> = {
  database: 'A query completed from the API process.',
  redis: 'Redis answered PING from the API process.',
  emailGateway: 'Last recorded gateway status; not a live send test.',
  queueSystem: 'The queue transport responded. This does not prove a consumer is running.',
  storage: 'The storage backend responded.',
};

const probeLabel = (key: string): string =>
  PROBE_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

type LoadState = 'loading' | 'ready' | 'error';

export function MonitoringPage() {
  const [health, setHealth] = useState<PlatformHealth | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');

    fetchPlatformHealth(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setHealth(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // An outage must never render as "healthy" — the inverse of the 12.2B
        // honesty rule. A failed probe fetch is itself a signal, not silence.
        setErrorMessage(getApiErrorMessage(error, 'Unable to load platform health.'));
        setHealth(null);
        setState('error');
      });

    return () => controller.abort();
  }, [reloadToken]);

  const probes = useMemo(
    () => Object.entries(health?.services ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [health],
  );

  const overall = health ? STATUS_META[health.overall] : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Monitoring</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Live platform infrastructure health. Current state only — no history is recorded.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={state === 'loading'}
          aria-label="Re-check platform health"
          className="inline-flex h-11 items-center gap-2 rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <FontAwesomeIcon icon={faArrowsRotate} className="h-3.5 w-3.5" />
          Re-check
        </button>
      </div>

      {/* ── Overall ─────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-card p-5">
        {state === 'loading' ? (
          <div data-testid="monitoring-loading">
            <SkeletonTable rows={5} cols={3} />
          </div>
        ) : state === 'error' ? (
          <div
            className="flex flex-col items-center justify-center px-4 py-12 text-center"
            data-testid="monitoring-error"
            role="status"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="h-4 w-4 text-muted-foreground"
              />
            </div>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">{errorMessage}</p>
            <button
              type="button"
              onClick={reload}
              className="mt-4 inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3" data-testid="monitoring-overall">
              <FontAwesomeIcon
                icon={overall?.icon ?? faHeartPulse}
                className={`h-5 w-5 ${overall?.tone ?? ''}`}
              />
              {/* Colour is never the sole indicator — the word is always present. */}
              <span className="text-base font-semibold text-foreground">
                {overall?.label ?? 'Unknown'}
              </span>
              <span className="text-sm text-muted-foreground" aria-live="polite">
                Checked {health?.checkedAt ? new Date(health.checkedAt).toLocaleString() : '—'} ·
                probes ran in {health?.apiResponseMs ?? 0} ms
              </span>
            </div>

            {probes.length === 0 && (
              <p className="mt-4 text-sm text-muted-foreground" data-testid="monitoring-empty">
                No health probes were reported.
              </p>
            )}
          </>
        )}
      </section>

      {/* ── Per-probe detail ────────────────────────────────────────────── */}
      {state === 'ready' && probes.length > 0 && (
        <section className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold text-foreground">Probes</h2>
          </div>
          <ul className="grid grid-cols-1 gap-3 p-5" data-testid="monitoring-probes">
            {probes.map(([key, probe]) => (
              <ProbeRow key={key} name={key} probe={probe} />
            ))}
          </ul>
        </section>
      )}

      {/* ── The honest limitation, stated on the page, not buried ───────── */}
      <section
        className="rounded-xl border border-border bg-card p-5"
        data-testid="monitoring-scope"
      >
        <h2 className="text-sm font-semibold text-foreground">
          What this page can and cannot prove
        </h2>
        <dl className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <div className="min-w-0">
            <dt className="font-medium text-foreground">Proven</dt>
            <dd className="mt-1 text-muted-foreground">
              Each dependency above was reachable from the API process at the moment shown, with the
              latency reported.
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="font-medium text-foreground">Not proven</dt>
            <dd className="mt-1 text-muted-foreground">
              Worker-process liveness. Queue processors run inside the API container, so this
              endpoint cannot independently confirm a consumer is draining a queue — the same
              process must be alive to answer at all. Queue depth on Background Jobs is the better
              signal.
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="font-medium text-foreground">Not recorded</dt>
            <dd className="mt-1 text-muted-foreground">
              Nothing on this page is stored. There is no health history, uptime figure or trend,
              and no alerting.
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="font-medium text-foreground">Email gateway</dt>
            <dd className="mt-1 text-muted-foreground">
              Reports the last recorded gateway status, not a live send test, so it carries no
              latency.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

function ProbeRow({ name, probe }: { name: string; probe: PlatformHealthProbe }) {
  const meta = STATUS_META[probe.status] ?? STATUS_META.down;
  return (
    <li
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`probe-${name}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={meta.icon} className={`h-3.5 w-3.5 ${meta.tone}`} />
          <span className="break-words text-sm font-medium text-foreground">
            {probeLabel(name)}
          </span>
          <span className={`text-xs font-semibold ${meta.tone}`}>{meta.label}</span>
        </div>
        <p className="mt-1 break-words text-xs text-muted-foreground">
          {probe.message ?? PROBE_MEANING[name] ?? 'Responded to its probe.'}
        </p>
      </div>
      <div className="shrink-0 text-xs text-muted-foreground">
        {probe.latencyMs === undefined ? (
          // Never invented: a stored status has no latency, and the backend
          // deliberately does not fabricate one.
          <span>No latency — stored status</span>
        ) : (
          <span>{probe.latencyMs} ms</span>
        )}
      </div>
    </li>
  );
}
